import moment from 'moment';

/**
 * Parse timezone string to UTC offset in minutes
 * Supports: "Africa/Johannesburg (UTC+2)" format passed from localStorage
 */
const parseTimezoneToMinutes = (tzString: string): number => {
  console.log('[momentTzPlugin] parseTimezoneToMinutes called with:', tzString);

  if (!tzString) {
    console.log('[momentTzPlugin] parseTimezoneToMinutes: Empty timezone string, returning 0');
    return 0;
  }

  // Extract from (UTC±X) pattern (e.g., "Africa/Johannesburg (UTC+2)")
  const utcMatch = tzString.match(/\(UTC([+-])(\d+(?:\.\d+)?)\)/);
  if (utcMatch) {
    const sign = utcMatch[1] === '+' ? 1 : -1;
    const hours = parseFloat(utcMatch[2]);
    const minutes = sign * hours * 60;
    console.log('[momentTzPlugin] parseTimezoneToMinutes: Matched UTC pattern, sign:', utcMatch[1], 'hours:', hours, 'result (minutes):', minutes);
    return minutes; // Convert to minutes
  }

  // Fallback for pure UTC format (e.g., "UTC+3")
  const cleanTz = tzString.toUpperCase().replace('UTC', '').trim();
  if (cleanTz && cleanTz !== '') {
    const offsetMatch = cleanTz.match(/([+-])(\d+(?:\.\d+)?)/);
    if (offsetMatch) {
      const sign = offsetMatch[1] === '+' ? 1 : -1;
      const hours = parseFloat(offsetMatch[2]);
      const minutes = sign * hours * 60;
      console.log('[momentTzPlugin] parseTimezoneToMinutes: Matched pure UTC format, sign:', offsetMatch[1], 'hours:', hours, 'result (minutes):', minutes);
      return minutes;
    }
  }

  return 0;
};

/** True when localStorage has a non-empty profile timezone (e.g. backoffice user settings). */
export function isActiveTimezoneConfigured(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return Boolean(localStorage.getItem('activeTz')?.trim());
}

// Store original format function
const originalFormat = moment.prototype.format;

/**
 * Create new function formatTZ() to automatically apply user's configured timezone from localStorage
 * This way ANY component using moment().formatTZ() will automatically get timezone conversion
 */
moment.fn.formatTZ = function (format: string): string {
  // Fallback to original format
  const fallbackResult = originalFormat.call(this, format);
  console.log('[momentTzPlugin] formatTZ called with format:', format);
  console.log('[momentTzPlugin] formatTZ - moment instance:', this.toString());

  try {
    // Get timezone from localStorage (already saved there when user updates profile)
    const timezone = typeof window !== 'undefined' ? localStorage.getItem('activeTz') : null;
    console.log('[momentTzPlugin] formatTZ - Retrieved timezone from localStorage:', timezone);

    if (!timezone) {
      return fallbackResult;
    }
    const offsetMinutes = parseTimezoneToMinutes(timezone);
    console.log('[momentTzPlugin] formatTZ - Calculated offset minutes:', offsetMinutes);

    if (offsetMinutes == 0) {
      return fallbackResult;
    }

    // Apply timezone offset
    const result = this.utc().utcOffset(offsetMinutes).format(format);
    console.log('[momentTzPlugin] formatTZ - Applied timezone offset, result:', result);
    return result;
  } catch (error) {
    // Silently fall back to original format if localStorage access fails
    return fallbackResult;
  }
};

/**
 * Also keep forceFormatTZ() method for explicit timezone override when needed
 */
moment.fn.forceFormatTZ = function (format: string, timezone: string = 'UTC'): string {
  const offsetMinutes = parseTimezoneToMinutes(timezone);
  const result = this.utc().utcOffset(offsetMinutes).format(format);
  return result;
};

// TypeScript declarations
declare global {
  namespace moment {
    interface Moment {
      formatTZ(format: string): string;
      forceFormatTZ(format: string, timezone: string): string;
    }
  }
}

/** UTC offset label for tooltip footers; uses activeTz when set, else browser offset. */
export function getConfiguredTimezoneOffsetLabel(): string {
  const timezone = isActiveTimezoneConfigured()
    ? localStorage.getItem('activeTz')?.trim()
    : null;

  if (timezone) {
    const utcMatch = timezone.match(/\(UTC([+-])(\d+(?:\.\d+)?)\)/);
    if (utcMatch) {
      const sign = utcMatch[1];
      const hours = parseFloat(utcMatch[2]);
      const wholeHours = Math.floor(hours);
      const minutes = Math.round((hours - wholeHours) * 60);
      return `UTC ${sign}${String(wholeHours).padStart(2, '0')}:${String(
        minutes
      ).padStart(2, '0')}`;
    }
    const cleanMatch = timezone.toUpperCase().match(/UTC([+-])(\d+(?:\.\d+)?)/);
    if (cleanMatch) {
      const sign = cleanMatch[1];
      const hours = parseFloat(cleanMatch[2]);
      const wholeHours = Math.floor(hours);
      const minutes = Math.round((hours - wholeHours) * 60);
      return `UTC ${sign}${String(wholeHours).padStart(2, '0')}:${String(
        minutes
      ).padStart(2, '0')}`;
    }
  }

  const offsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
  const offsetMinutesRemainder = Math.abs(offsetMinutes % 60);
  const sign = offsetMinutes < 0 ? '+' : '-';
  return `UTC ${sign}${String(offsetHours).padStart(2, '0')}:${String(
    offsetMinutesRemainder
  ).padStart(2, '0')}`;
}

export default moment;
